<template>
  <v-dialog
    scrollable
    max-width="650"
    v-if="show"
    :value="show"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card data-tourId="problemSolving-5whys-tool">
      <div class="py-6 px-8">
        <v-card-title class="ma-0 pa-0">
          {{ title }}
        </v-card-title>
        <v-card-subtitle class="ma-0 pa-0"> {{ subtitle }}</v-card-subtitle>
      </div>
      <v-divider class="ma-0 mb-3" />
      <v-card-text class="ma-0 px-8">
        <QuestionsStep
          v-if="currentStep === STEPS.QUESTIONS"
          data-tourId="problemSolving-5whys-questions"
          :questions="questions"
          :isEditMode="isEditMode"
          @removeWhy="handleRemoveWhy"
          @close="handleClose"
        />
        <SummaryStep
          v-if="currentStep === STEPS.SUMMARY"
          :isEditMode="isEditMode"
          :rootCause="rootCauseToUpdate"
        />
      </v-card-text>
      <v-divider class="ma-0" />
      <v-card-actions class="justify-end ma-0 px-8 py-6">
        <FiveWhysToolActions
          :currentStep="currentStep"
          :isEditMode="isEditMode"
          :steps="STEPS"
          :canGoToNextStep="canGoToNextStep"
          @back="handleBack"
          @next="handleNext"
          @finish="handleFinish"
          @close="handleClose"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import QuestionsStep from './QuestionsStep.vue'
import SummaryStep from './SummaryStep.vue'
import FiveWhysToolActions from './FiveWhysToolActions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

const STEPS = {
  QUESTIONS: 'questions',
  SUMMARY: 'summary',
}

export default {
  name: 'FiveWhysToolModal',

  mixins: [trackerMixin],

  components: {
    QuestionsStep,
    SummaryStep,
    FiveWhysToolActions,
  },

  props: {
    show: {
      type: Boolean,
      required: true,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      required: true,
    },
    rootCause: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      STEPS,
      currentStep: STEPS.QUESTIONS,
      title: this.$t('problem_solving.analyse_tool.5w_tool_name'),
      questions: [],
      subtitle: this.$t('problem_solving.analyse_tool.instructions'),
    }
  },

  mounted() {
    this.setInitialAnswers()
  },

  computed: {
    rootCauseToUpdate() {
      return { ...this.rootCause }
    },
    canGoToNextStep() {
      return this.questions
        ?.map(question => Boolean(question?.answer?.trim()))
        .includes(true)
    },
  },

  methods: {
    setInitialAnswers() {
      const answers = this.rootCause?.analysisWhyBreakdown?.map(answer => {
        return {
          answer,
        }
      })

      this.questions = answers?.length
        ? answers
        : [{ answer: '' }, { answer: '' }, { answer: '' }]
    },

    handleNext() {
      this.currentStep = STEPS.SUMMARY
      this.title = this.$t('common.summary')
      this.subtitle = this.$t(
        'problem_solving.analyse_tool.summary_description'
      )
      this.updateAnalysisWhyBreakdown()
      this.trackTool('5Whys', 'next', 'problem-solving')
    },

    handleBack() {
      this.currentStep = STEPS.QUESTIONS
      this.title = this.$t('problem_solving.analyse_tool.5w_tool_name')
      this.subtitle = this.$t('problem_solving.analyse_tool.instructions')
      this.trackTool('5Whys', 'back', 'problem-solving')
    },

    updateAnalysisWhyBreakdown() {
      const analysisWhyBreakdown = this.questions.map(
        question => question?.answer ?? ''
      )

      this.$emit(
        'setAnalysisWhyBreakdown',
        analysisWhyBreakdown.filter(answer => Boolean(answer?.length))
      )
    },

    handleClose() {
      this.$emit('close')
      this.currentStep = STEPS.QUESTIONS
    },

    handleFinish() {
      this.$emit('updateRootCause', this.rootCauseToUpdate)

      this.handleClose()

      this.trackTool('5Whys', 'finish', 'problem-solving')
    },

    handleRemoveWhy(index) {
      if (index > 0) return this.questions.splice(index, 1)
      if (index === 0) {
        this.questions.splice(index, 1)
        this.updateAnalysisWhyBreakdown()
        this.trackTool('5Whys', 'next', 'problem-solving')
      }
    },
  },
}
</script>
